import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
    BookOpen, Plus, Search, Loader2, MoreHorizontal, Copy, Trash2,
    Clock, CheckCircle2, Archive, FileText, Target, Handshake, Presentation, BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MEETING_TYPES = {
    discovery: { label: 'Discovery', icon: Search, color: 'bg-blue-100 text-blue-700' },
    demo: { label: 'Demo', icon: Presentation, color: 'bg-purple-100 text-purple-700' },
    negotiation: { label: 'Negotiation', icon: Handshake, color: 'bg-amber-100 text-amber-700' },
    closing: { label: 'Closing', icon: Target, color: 'bg-emerald-100 text-emerald-700' },
    qbr: { label: 'QBR', icon: BarChart3, color: 'bg-rose-100 text-rose-700' },
    custom: { label: 'Custom', icon: FileText, color: 'bg-stone-100 text-stone-600' },
};

const STATUS_MAP = {
    active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
    draft: { label: 'Draft', color: 'bg-stone-100 text-stone-500', icon: Clock },
    archived: { label: 'Archived', color: 'bg-stone-100 text-stone-400', icon: Archive },
};

function PlaybookCard({ playbook, onNavigate, onDuplicate, onDelete }) {
    const [showMenu, setShowMenu] = useState(false);
    const typeConf = MEETING_TYPES[playbook.meeting_type] || MEETING_TYPES.custom;
    const statusConf = STATUS_MAP[playbook.status] || STATUS_MAP.draft;
    const TypeIcon = typeConf.icon;
    const StatusIcon = statusConf.icon;

    const totalQuestions = (playbook.stages || []).reduce(
        (sum, s) => sum + (s.questions || []).length, 0
    );

    return (
        <div
            className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer group relative"
            onClick={() => onNavigate(playbook.playbook_id)}
        >
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${typeConf.color}`}>
                        <TypeIcon className="w-3 h-3" /> {typeConf.label}
                    </span>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusConf.color}`}>
                        <StatusIcon className="w-3 h-3" /> {statusConf.label}
                    </span>
                    {playbook.is_default && (
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-violet-100 text-violet-700">Default</span>
                    )}
                </div>
                <div className="relative" onClick={e => e.stopPropagation()}>
                    <button onClick={() => setShowMenu(!showMenu)} className="p-1 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-stone-100 transition-all">
                        <MoreHorizontal className="w-4 h-4 text-stone-400" />
                    </button>
                    {showMenu && (
                        <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-lg shadow-lg py-1 z-20 w-40">
                            <button onClick={() => { setShowMenu(false); onDuplicate(playbook.playbook_id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-stone-600 hover:bg-stone-50">
                                <Copy className="w-3.5 h-3.5" /> Duplicate
                            </button>
                            <button onClick={() => { setShowMenu(false); onDelete(playbook.playbook_id); }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-xs text-red-600 hover:bg-red-50">
                                <Trash2 className="w-3.5 h-3.5" /> Archive
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex items-start gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-100 to-indigo-50 border border-blue-200 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <div className="min-w-0">
                    <h3 className="text-sm font-bold text-stone-900 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {playbook.name}
                    </h3>
                    <p className="text-xs text-stone-500 line-clamp-2 mt-0.5">
                        {playbook.description || 'No description'}
                    </p>
                </div>
            </div>

            <div className="flex items-center gap-4 text-[10px] text-stone-400 pt-3 border-t border-stone-100">
                <span>{(playbook.stages || []).length} stages</span>
                <span>{totalQuestions} questions</span>
                {playbook.created_by_name && <span>by {playbook.created_by_name}</span>}
            </div>
        </div>
    );
}

export default function PlaybookList() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [playbooks, setPlaybooks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterType, setFilterType] = useState('');

    useEffect(() => { loadPlaybooks(); }, []);

    const loadPlaybooks = async () => {
        try {
            const res = await axios.get(`${API}/playbooks`, { headers: getAuthHeader() });
            setPlaybooks(res.data.playbooks || []);
        } catch (err) {
            toast.error('Failed to load playbooks');
        } finally {
            setLoading(false);
        }
    };

    const createPlaybook = async () => {
        try {
            const res = await axios.post(`${API}/playbooks`, {
                name: 'New Playbook',
                meeting_type: 'discovery',
                status: 'draft',
                stages: [],
            }, { headers: getAuthHeader() });
            navigate(`/playbooks/${res.data.playbook_id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create playbook');
        }
    };

    const duplicatePlaybook = async (id) => {
        try {
            await axios.post(`${API}/playbooks/${id}/duplicate`, {}, { headers: getAuthHeader() });
            toast.success('Playbook duplicated');
            loadPlaybooks();
        } catch (err) {
            toast.error('Failed to duplicate');
        }
    };

    const deletePlaybook = async (id) => {
        if (!window.confirm('Archive this playbook?')) return;
        try {
            await axios.delete(`${API}/playbooks/${id}`, { headers: getAuthHeader() });
            toast.success('Playbook archived');
            setPlaybooks(prev => prev.filter(p => p.playbook_id !== id));
        } catch (err) {
            toast.error('Failed to archive');
        }
    };

    const filtered = playbooks.filter(p => {
        if (filterType && p.meeting_type !== filterType) return false;
        if (search && !p.name.toLowerCase().includes(search.toLowerCase())) return false;
        return true;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Sales Playbooks
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">
                        Create structured question templates for different meeting types
                    </p>
                </div>
                <Button onClick={createPlaybook} className="gap-2">
                    <Plus className="w-4 h-4" /> New Playbook
                </Button>
            </div>

            <div className="flex items-center gap-3">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search playbooks..." className="pl-9" />
                </div>
                <div className="flex items-center gap-1">
                    {['', 'discovery', 'demo', 'negotiation', 'closing', 'qbr', 'custom'].map(t => (
                        <button key={t} onClick={() => setFilterType(t)}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                filterType === t ? 'bg-stone-900 text-white' : 'text-stone-500 hover:bg-stone-100 hover:text-stone-700'
                            }`}>
                            {t ? (MEETING_TYPES[t]?.label || t) : 'All'}
                        </button>
                    ))}
                </div>
            </div>

            {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-48 text-stone-400">
                    <BookOpen className="w-10 h-10 mb-3 text-stone-300" />
                    <p className="text-sm font-medium">
                        {playbooks.length === 0 ? 'No playbooks yet' : 'No matching playbooks'}
                    </p>
                    {playbooks.length === 0 && (
                        <p className="text-xs mt-1">Create your first sales playbook to guide meeting conversations</p>
                    )}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filtered.map(p => (
                        <PlaybookCard
                            key={p.playbook_id}
                            playbook={p}
                            onNavigate={id => navigate(`/playbooks/${id}`)}
                            onDuplicate={duplicatePlaybook}
                            onDelete={deletePlaybook}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}
